/**
 * useSpawnBatches Hook
 *
 * Provides CRUD operations and data fetching for spawn batches via Edge Function.
 * Part of US-P2-002: Spawn Production Tracking
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

/**
 * Call spawn-batches Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callSpawnBatchesFunction(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('spawn-batches', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch all spawn batches with optional filtering
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by status
 * @param {string} options.strainId - Filter by strain
 * @param {boolean} options.readyOnly - Only show ready batches
 * @param {number} options.limit - Limit results
 * @returns {Object} { spawnBatches, loading, error, refetch }
 */
export function useSpawnBatches(options = {}) {
  const [spawnBatches, setSpawnBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSpawnBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('list', {
        status: options.status,
        strain_id: options.strainId,
        ready_only: options.readyOnly,
        limit: options.limit || 50
      })

      setSpawnBatches(response.data || [])
    } catch (err) {
      console.error('[useSpawnBatches] Error:', err)
      setError(err.message)
      setSpawnBatches([])
    } finally {
      setLoading(false)
    }
  }, [options.status, options.strainId, options.readyOnly, options.limit])

  useEffect(() => {
    fetchSpawnBatches()
  }, [fetchSpawnBatches])

  return {
    spawnBatches,
    loading,
    error,
    refetch: fetchSpawnBatches
  }
}

/**
 * Hook to fetch spawn inventory statistics
 * @returns {Object} { stats, loading, error, refetch }
 */
export function useSpawnStats() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('stats')
      setStats(response.data)
    } catch (err) {
      console.error('[useSpawnStats] Error:', err)
      setError(err.message)
      setStats(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  }
}

/**
 * Hook for spawn batch mutations (create, update, use)
 * @returns {Object} Mutation functions and state
 */
export function useSpawnMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new spawn batch from a liquid culture
   * @param {Object} data - Spawn batch data
   * @param {string} data.liquid_culture_id - Source LC UUID
   * @param {string} data.strain_id - Strain UUID
   * @param {number} data.bag_count - Number of spawn bags
   * @param {string} data.grain_type - Type of grain (rye, millet, wheat, oat, sorghum)
   * @param {number} data.bag_weight_kg - Weight per bag in kg
   * @param {string} data.inoculation_date - Inoculation date (YYYY-MM-DD)
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Created spawn batch
   */
  const createSpawnBatch = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('create', data)
      return response.data
    } catch (err) {
      console.error('[useSpawnMutations.create] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing spawn batch
   * @param {Object} data - Update data
   * @param {string} data.id - Spawn batch UUID
   * @param {string} data.status - New status
   * @param {number} data.bags_remaining - Remaining bags
   * @param {string} data.actual_ready_date - Actual ready date
   * @param {string} data.notes - Notes
   * @returns {Promise<Object>} Updated spawn batch
   */
  const updateSpawnBatch = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('update', data)
      return response.data
    } catch (err) {
      console.error('[useSpawnMutations.update] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Record spawn usage (decrement remaining weight)
   * @param {Object} data - Usage data
   * @param {string} data.spawn_batch_id - Spawn batch UUID
   * @param {number} data.weight_used_kg - Weight used in kg
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Usage result
   */
  const useSpawn = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('use', data)
      return response.data
    } catch (err) {
      console.error('[useSpawnMutations.use] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Mark spawn batch as colonised (colonisation complete)
   * @param {string} id - Spawn batch UUID
   * @param {string} actualReadyDate - Optional actual colonised date
   * @returns {Promise<Object>} Updated spawn batch
   */
  const markAsColonised = useCallback(async (id, actualReadyDate = null) => {
    return updateSpawnBatch({
      id,
      status: 'colonised',
      actual_ready_date: actualReadyDate || toLocalDateString(new Date())
    })
  }, [updateSpawnBatch])

  /**
   * Mark spawn batch as contaminated
   * @param {string} id - Spawn batch UUID
   * @param {string} notes - Contamination notes
   * @returns {Promise<Object>} Updated spawn batch
   */
  const markAsContaminated = useCallback(async (id, notes = '') => {
    return updateSpawnBatch({
      id,
      status: 'contaminated',
      notes
    })
  }, [updateSpawnBatch])

  /**
   * Convert shadow batches to real spawn production
   * @param {string[]} batchIds - Array of shadow batch UUIDs to convert
   * @param {Object} options - Optional settings
   * @param {string} options.liquidCultureId - Optional LC to assign to all batches
   * @returns {Promise<Object[]>} Results for each batch { batch_id, batch_code, success, error_message }
   */
  const convertShadowToSpawn = useCallback(async (batchIds, options = {}) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase.rpc('convert_shadow_batches_to_spawn', {
        p_batch_ids: batchIds,
        p_liquid_culture_id: options.liquidCultureId || null,
        p_started_by: null // TODO: Get current user ID
      })

      if (rpcError) {
        throw new Error(rpcError.message)
      }

      // Check if any conversions failed
      const failures = data?.filter(r => !r.success) || []
      if (failures.length > 0) {
        const errorMessages = failures.map(f => `${f.batch_code}: ${f.error_message}`).join(', ')
        console.warn('[convertShadowToSpawn] Some batches failed:', errorMessages)
      }

      return data || []
    } catch (err) {
      console.error('[useSpawnMutations.convertShadowToSpawn] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createSpawnBatch,
    updateSpawnBatch,
    useSpawn,
    markAsColonised,
    markAsContaminated,
    convertShadowToSpawn
  }
}

/**
 * Hook to fetch spawn batches available for use (colonised status with remaining weight)
 * @param {string} strainId - Optional strain filter
 * @returns {Object} { availableSpawn, loading, error, refetch }
 */
export function useAvailableSpawn(strainId = null) {
  const [availableSpawn, setAvailableSpawn] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAvailable = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSpawnBatchesFunction('list', {
        ready_only: true,
        strain_id: strainId
      })

      // Filter to only include batches with remaining weight
      const available = (response.data || []).filter(
        batch => batch.remaining_weight_kg > 0
      )

      setAvailableSpawn(available)
    } catch (err) {
      console.error('[useAvailableSpawn] Error:', err)
      setError(err.message)
      setAvailableSpawn([])
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    fetchAvailable()
  }, [fetchAvailable])

  return {
    availableSpawn,
    loading,
    error,
    refetch: fetchAvailable
  }
}

export default useSpawnBatches
